<template>
  <div class="MdImg"
    :class="[
      image.position === 'absolute' ? 'ExAbs' : '',
      flexClass,
      marginClass,
      alignClass,
      gravityClass,
      sizeClass,
      aspectModeClass,
      offsetTopClass,
      offsetBottomClass,
      offsetStartClass,
      offsetEndClass,
      hoverPath === parentPath + 'image' ? 'on-hover' : '',
      !isAdmin && (editPath === videoPath || editPath === parentPath + 'image') ? 'on-edit' : ''
    ]"
    :style="styles"
    @click="selectNode(image, parentPath + 'image')"
  >
    <div>
      <a :style="aspectRatioStyle">
        <span :style="{
          backgroundImage: `url('${base64Image || proxiedImageUrl}')`,
          backgroundColor: image.backgroundColor
        }"></span>
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import { Prop, Watch } from 'vue-property-decorator'
import { IFlexMessageImage } from '@/utils/types'
import cloneDeep from 'lodash/cloneDeep'

@Options({
  components: {},
  directives: { maska },
  emits: ['onSelectNode', 'initListNode'],
})
export default class Image extends Vue {
  @Prop()
  image!: IFlexMessageImage

  @Prop()
  parentLayout!: string

  @Prop()
  direction!: string

  @Prop()
  parentPath!: string

  @Prop()
  hoverPath!: string

  @Prop()
  editPath!: string

  @Prop()
  isAdmin!: boolean

  base64Image: string | null = null

  get proxiedImageUrl() {
    if (this.image.url) {
      const baseUrl = process.env.VUE_APP_API_ENDPOINT
      return `${baseUrl}v1/proxy_image?url=` + encodeURIComponent(this.image.url)
    }
    return ''
  }

  get marginClass() {
    if (this.parentLayout === '') {
      return ''
    } else if (this.parentLayout === 'horizontal' || this.parentLayout === 'baseline') {
      if (this.direction === 'rtl') {
        if (this.image.margin === 'none') {
          return 'ExMgnRNone'
        } else if (this.image.margin === 'xs') {
          return 'ExMgnRXs'
        } else if (this.image.margin === 'sm') {
          return 'ExMgnRSm'
        } else if (this.image.margin === 'md') {
          return 'ExMgnRMd'
        } else if (this.image.margin === 'lg') {
          return 'ExMgnRLg'
        } else if (this.image.margin === 'xl') {
          return 'ExMgnRXl'
        } else if (this.image.margin === 'xxl') {
          return 'ExMgnRXXl'
        }
      } else {
        if (this.image.margin === 'none') {
          return 'ExMgnLNone'
        } else if (this.image.margin === 'xs') {
          return 'ExMgnLXs'
        } else if (this.image.margin === 'sm') {
          return 'ExMgnLSm'
        } else if (this.image.margin === 'md') {
          return 'ExMgnLMd'
        } else if (this.image.margin === 'lg') {
          return 'ExMgnLLg'
        } else if (this.image.margin === 'xl') {
          return 'ExMgnLXl'
        } else if (this.image.margin === 'xxl') {
          return 'ExMgnLXXl'
        }
      }
    } else {
      if (this.image.margin === 'none') {
        return 'ExMgnTNone'
      } else if (this.image.margin === 'xs') {
        return 'ExMgnTXs'
      } else if (this.image.margin === 'sm') {
        return 'ExMgnTSm'
      } else if (this.image.margin === 'md') {
        return 'ExMgnTMd'
      } else if (this.image.margin === 'lg') {
        return 'ExMgnTLg'
      } else if (this.image.margin === 'xl') {
        return 'ExMgnTXl'
      } else if (this.image.margin === 'xxl') {
        return 'ExMgnTXXl'
      }
    }
  }

  get alignClass() {
    if (this.image.align === 'start') {
      return 'algS'
    } else if (this.image.align === 'end') {
      return 'algE'
    }
    return ''
  }

  get gravityClass() {
    if (this.image.gravity === 'bottom') {
      return 'grvB'
    } else if (this.image.gravity === 'center') {
      return 'grvC'
    }
    return ''
  }

  get sizeClass() {
    if (this.image.size === 'xxs') {
      return 'ExXXs'
    } else if (this.image.size === 'xs') {
      return 'ExXs'
    } else if (this.image.size === 'sm') {
      return 'ExSm'
    } else if (this.image.size === 'md') {
      return 'ExMd'
    } else if (this.image.size === 'lg') {
      return 'ExLg'
    } else if (this.image.size === 'xl') {
      return 'ExXl'
    } else if (this.image.size === 'xxl') {
      return 'ExXXl'
    } else if (this.image.size === '3xl') {
      return 'Ex3Xl'
    } else if (this.image.size === '4xxl') {
      return 'Ex4Xl'
    } else if (this.image.size === '5xl') {
      return 'Ex5Xl'
    } else if (this.image.size === 'full') {
      return 'ExFull'
    } else {
      return 'ExMd'
    }
  }

  get aspectRatioStyle() {
    if (this.image.aspectRatio) {
      const [width, height] = this.image.aspectRatio.split(':').map(Number)
      return {
        paddingBottom: height / width * 100 + '%',
      }
    }
    return {
      paddingBottom: '100%',
    }
  }

  get aspectModeClass() {
    if (this.image.aspectMode === 'cover') {
      return 'ExCover'
    }
    return 'ExFit'
  }

  get offsetTopClass() {
    if (this.image.offsetTop === 'none') {
      return 'ExTNone'
    } else if (this.image.offsetTop === 'xs') {
      return 'ExTXs'
    } else if (this.image.offsetTop === 'sm') {
      return 'ExTSm'
    } else if (this.image.offsetTop === 'md') {
      return 'ExTMd'
    } else if (this.image.offsetTop === 'lg') {
      return 'ExTLg'
    } else if (this.image.offsetTop === 'xl') {
      return 'ExTXl'
    } else if (this.image.offsetTop === 'xxl') {
      return 'ExTXXl'
    } else {
      return ''
    }
  }

  get offsetBottomClass() {
    if (this.image.offsetBottom === 'none') {
      return 'ExBNone'
    } else if (this.image.offsetBottom === 'xs') {
      return 'ExBXs'
    } else if (this.image.offsetBottom === 'sm') {
      return 'ExBSm'
    } else if (this.image.offsetBottom === 'md') {
      return 'ExBMd'
    } else if (this.image.offsetBottom === 'lg') {
      return 'ExBLg'
    } else if (this.image.offsetBottom === 'xl') {
      return 'ExBXl'
    } else if (this.image.offsetBottom === 'xxl') {
      return 'ExBXXl'
    } else {
      return ''
    }
  }

  get offsetStartClass() {
    if (this.direction === 'rtl') {
      if (this.image.offsetStart === 'none') {
        return 'ExRNone'
      } else if (this.image.offsetStart === 'xs') {
        return 'ExRXs'
      } else if (this.image.offsetStart === 'sm') {
        return 'ExRSm'
      } else if (this.image.offsetStart === 'md') {
        return 'ExRMd'
      } else if (this.image.offsetStart === 'lg') {
        return 'ExRLg'
      } else if (this.image.offsetStart === 'xl') {
        return 'ExRXl'
      } else if (this.image.offsetStart === 'xxl') {
        return 'ExRXXl'
      } else {
        return ''
      }
    } else {
      if (this.image.offsetStart === 'none') {
        return 'ExLNone'
      } else if (this.image.offsetStart === 'xs') {
        return 'ExLXs'
      } else if (this.image.offsetStart === 'sm') {
        return 'ExLSm'
      } else if (this.image.offsetStart === 'md') {
        return 'ExLMd'
      } else if (this.image.offsetStart === 'lg') {
        return 'ExLLg'
      } else if (this.image.offsetStart === 'xl') {
        return 'ExLXl'
      } else if (this.image.offsetStart === 'xxl') {
        return 'ExLXXl'
      } else {
        return ''
      }
    }
  }

  get offsetEndClass() {
    if (this.direction === 'rtl') {
      if (this.image.offsetEnd === 'none') {
        return 'ExLNone'
      } else if (this.image.offsetEnd === 'xs') {
        return 'ExLXs'
      } else if (this.image.offsetEnd === 'sm') {
        return 'ExLSm'
      } else if (this.image.offsetEnd === 'md') {
        return 'ExLMd'
      } else if (this.image.offsetEnd === 'lg') {
        return 'ExLLg'
      } else if (this.image.offsetEnd === 'xl') {
        return 'ExLXl'
      } else if (this.image.offsetEnd === 'xxl') {
        return 'ExLXXl'
      } else {
        return ''
      }
    } else {
      if (this.image.offsetEnd === 'none') {
        return 'ExRNone'
      } else if (this.image.offsetEnd === 'xs') {
        return 'ExRXs'
      } else if (this.image.offsetEnd === 'sm') {
        return 'ExRSm'
      } else if (this.image.offsetEnd === 'md') {
        return 'ExRMd'
      } else if (this.image.offsetEnd === 'lg') {
        return 'ExRLg'
      } else if (this.image.offsetEnd === 'xl') {
        return 'ExRXl'
      } else if (this.image.offsetEnd === 'xxl') {
        return 'ExRXXl'
      } else {
        return ''
      }
    }
  }

  get flexClass() {
    if (this.image.flex === 0) {
      return 'fl0'
    } else if (this.image.flex === 1) {
      return 'fl1'
    } else if (this.image.flex === 2) {
      return 'fl2'
    } else if (this.image.flex === 3) {
      return 'fl3'
    }
    return ''
  }

  get styles() {
    const imageStyles: Record<string, string|number|undefined> = {}

    imageStyles.flexGrow = this.image.flex
    imageStyles.cursor = 'pointer'
    imageStyles.top = this.image.offsetTop
    imageStyles.bottom = this.image.offsetBottom
    if (this.direction === 'rtl') {
      imageStyles.left = this.image.offsetEnd
      imageStyles.right = this.image.offsetStart
    } else {
      imageStyles.left = this.image.offsetStart
      imageStyles.right = this.image.offsetEnd
    }

    return imageStyles
  }

  get currentNodePath() {
    return this.parentPath + 'image'
  }

  get videoPath() {
    if (this.parentPath.includes('.altContent.')) {
      return cloneDeep(this.parentPath).slice(0, -1)
    }
    return ''
  }

  @Watch('image.url', { immediate: true })
  async fetchBase64Image() {
    if (this.proxiedImageUrl) {
      this.base64Image = await this.loadImageAsBase64(this.proxiedImageUrl)
    }
  }

  async loadImageAsBase64(url: string): Promise<string | null> {
    const response = await fetch(url)
    const blob = await response.blob()

    return await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const result = reader.result
        if (typeof result === 'string') {
          resolve(result)
        } else {
          resolve(null)
        }
      }
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  }

  selectNode(node, path) {
    this.$emit('onSelectNode', node, path)
  }

  async mounted() {
    if (this.videoPath) {
      this.$emit('initListNode', this.videoPath)
    }
    await this.fetchBase64Image()
  }
}
</script>

<style scoped lang="scss">

</style>
